import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, FormGroup, Input } from 'reactstrap';

import apiOffers from 'api/offers';
import IOfferInfoRow from 'api/interfaces/offers/IOfferInfoRow';
import LinkButton from 'components/layout/buttons/LinkButton';
import AutoGrowTextArea from 'components/layout/forms/AutoGrowTextArea';
import translations from 'services/translations/translations';

import 'style/offers/OfferInfoRowFields.css';

const MAX_TITLE_LENGTH = 100;
const MAX_TEXT_LENGTH = 1000;

/**
 * The own rows of the information table of the offer PDF (printed after the collaterals) as
 * fields among the other PDF fields of the basic information: the title in place of the
 * label, the text in the field. The rows of the department's offer defaults that the offer
 * does not have yet can be added with one click.
 */
const OfferInfoRowFields = ({
  columnWidth,
  disabled,
  onChange,
  rows,
}: {
  // The width of a field in the columns of the form (12 in all)
  columnWidth: number;
  disabled?: boolean;
  onChange: (rows: IOfferInfoRow[]) => void;
  rows: IOfferInfoRow[];
}): JSX.Element => {
  const { t } = useTranslation([translations.offers]);
  const tr = (key: string) => t(`${translations.offers}:infoRows.${key}`);
  const [defaultRows, setDefaultRows] = useState<IOfferInfoRow[]>([]);

  useEffect(() => {
    if (disabled) {
      return undefined;
    }

    let active = true;
    apiOffers.defaults
      .get()
      .then((defaults) => {
        if (active) {
          setDefaultRows(defaults.infoRows ?? []);
        }
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [disabled]);

  const titles = rows.map((row) => row.title.trim().toLowerCase());
  const missingDefaults = defaultRows.filter(
    (row) => !titles.includes(row.title.trim().toLowerCase())
  );

  const change = (index: number, field: keyof IOfferInfoRow, value: string) =>
    onChange(
      rows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      )
    );

  return (
    <>
      {rows.map((row, index) => (
        // The rows have no ids; they are edited in place
        // eslint-disable-next-line react/no-array-index-key
        <Col className="offer-info-field" key={index} md={columnWidth}>
          <FormGroup>
            <div className="offer-info-field-heading">
              <Input
                aria-label={tr('Title')}
                className="offer-info-field-title"
                disabled={disabled}
                maxLength={MAX_TITLE_LENGTH}
                onChange={(event) => change(index, 'title', event.target.value)}
                placeholder={tr('Title')}
                value={row.title}
              />
              {disabled ? null : (
                <button
                  aria-label={tr('Remove')}
                  className="offer-info-field-remove"
                  onClick={() =>
                    onChange(rows.filter((_, rowIndex) => rowIndex !== index))
                  }
                  title={tr('Remove')}
                  type="button"
                >
                  ✕
                </button>
              )}
            </div>
            {/* A long text wraps and the field grows with it */}
            <AutoGrowTextArea
              ariaLabel={row.title || tr('Text')}
              disabled={disabled}
              maxLength={MAX_TEXT_LENGTH}
              onChange={(event) => change(index, 'text', event.target.value)}
              placeholder={tr('Text')}
              title={row.text}
              value={row.text}
            />
          </FormGroup>
        </Col>
      ))}
      {disabled ? null : (
        <Col className="offer-info-field-actions" md={columnWidth}>
          <LinkButton
            onClick={() => onChange([...rows, { text: '', title: '' }])}
          >
            {`+ ${tr('Add row')}`}
          </LinkButton>
          {missingDefaults.length === 0 ? null : (
            <LinkButton onClick={() => onChange([...rows, ...missingDefaults])}>
              {`+ ${tr('Add default rows')} (${missingDefaults.length})`}
            </LinkButton>
          )}
        </Col>
      )}
    </>
  );
};

export default OfferInfoRowFields;
